/**
 * Friendly-target frame (#333) — the "party target" analogue of `TargetFrame`
 * for the ALLY slot (`targets.friendly`).
 *
 * Origin: pressing the default "Target party 1" keybind (`j`,
 * `apps/client/src/input/keybinding/keymap.ts`) mechanically DOES select a
 * friendly target — in a 1v1 (no other allies) it always resolves to
 * yourself, because `allyRoster` (packages/sim/src/targeting.ts)
 * unconditionally pins self as roster index 0. That mechanic was already
 * correct and tested; the gap was that NOTHING in the HUD showed it had any
 * effect, so pressing the key looked indistinguishable from doing nothing.
 * This frame closes that gap: whenever `targets.friendly` resolves to an
 * entity, show it here, with an explicit "you" label when it resolves to the
 * caster's own entity (the common 1v1 self-heal case).
 *
 * Mirrors TargetFrame's layout/data-* contract 1:1 (hp/cast/resource bars),
 * swapped to the friendly slot — see TargetFrame.tsx for the reference
 * pattern this was authored against.
 *
 * Layout (#421): no longer self-positions (`position: absolute` + a
 * hardcoded `left: %`) — renders as an ordinary flex child of
 * `FrameCluster`'s friendly (left) group, alongside `OwnFrame`, extending the
 * same friendly-left/enemy-right convention `UnitFrameStack` (#138) already
 * used for the bottom roster.
 *
 * Champion resolution (#420 fix): the resource bar + cast-bar spell name now
 * resolve against the FRIENDLY TARGET'S OWN `championId` (`WireEntity.
 * championId`, #420) instead of the hardcoded `DEFAULT_HUD_CHAMPION_ID`
 * placeholder — same bug class as #275's own-frame regression / TargetFrame's
 * #420 fix.
 *
 * PURELY COSMETIC / CLIENT-SIDE. No sim, no protocol, no hash, no
 * SIM_VERSION — reads only the already-wired `targets.friendly` field.
 *
 * data-* contract (component + e2e assertions):
 *   • data-testid="friendly-target-frame"
 *   • data-empty — "true" when no friendly target is selected
 *   • data-self — "true" when the friendly target IS the caster (#333's
 *     motivating case)
 *   • data-hp — integer hp value
 *   • data-max-hp — integer max hp value
 */

import type { ReactElement } from "react";
import { useStore } from "../store/context.js";
import { friendlyTargetEntity, ownEntity } from "./hud-util.js";
import { FrameCastBar } from "./FrameCastBar.js";
import { FrameResourceBar } from "./FrameResourceBar.js";
import { HpBar } from "./HpBar.js";

/**
 * Friendly-slot accent — matches `UnitFrameStack`'s `friendlyBorderColor`
 * (#3aa675) so the "this is a friendly target" cue stays visually
 * consistent across the two HUD surfaces.
 */
const FRIENDLY_ACCENT = "#3aa675";

export function FriendlyTargetFrame(): ReactElement {
  const { state } = useStore();
  const me = ownEntity(state.hudSnapshot, state.ownSeat);
  const target = friendlyTargetEntity(state.hudSnapshot, state.ownSeat);
  const isEmpty = target === null;
  const isSelf = target !== null && me !== null && target.entityId === me.entityId;
  return (
    <div
      data-testid="friendly-target-frame"
      data-empty={isEmpty ? "true" : "false"}
      data-self={isSelf ? "true" : "false"}
      data-hp={target === null ? "0" : String(target.hp)}
      data-max-hp={target === null ? "0" : String(target.maxHp)}
      style={{
        width: 200,
        padding: 8,
        background: "#161c24",
        border: `1px solid ${FRIENDLY_ACCENT}`,
        borderRadius: 4,
        opacity: isEmpty ? 0.4 : 1,
        pointerEvents: "auto",
      }}
    >
      {isEmpty || target === null ? (
        <div style={{ fontSize: 12, opacity: 0.7 }}>no friendly target</div>
      ) : (
        <>
          <div style={{ fontSize: 12, marginBottom: 4, color: FRIENDLY_ACCENT }}>
            friendly target · {isSelf ? "you" : `seat ${String(target.seat)}`}
          </div>
          <HpBar hp={target.hp} maxHp={target.maxHp} testId="friendly-target-hp-bar" />
          <FrameResourceBar
            resource={target.resource}
            points={target.points}
            championId={target.championId}
            testIdPrefix="friendly-target"
          />
          <FrameCastBar
            cast={target.cast}
            tick={state.hudSnapshot?.tick ?? 0}
            championId={target.championId}
            testIdPrefix="friendly-target-frame"
          />
        </>
      )}
    </div>
  );
}
